/**
 * Helpers for external orders (received via POST /api/v1/orders/external, e.g. AudioEcho
 * phone orders). External orders carry orderSource (the integration channel, e.g. "phone"),
 * a source object, deliveryType, orderCustomer (contact + address) and payment.method
 * onPickup/inStore — none of which kiosk orders have.
 */

/** Kiosk orders use orderSource "smartkiosk"; anything else non-empty is external. */
export const isExternalOrder = (order) =>
  !!order?.orderCustomer ||
  !!order?.source?.system ||
  (typeof order?.orderSource === "string" &&
    order.orderSource !== "" &&
    order.orderSource !== "smartkiosk");

const DELIVERY_TYPE_LABEL_KEYS = {
  delivery: "pages.dsokOrders.external.deliveryTypeDelivery",
  pickup: "pages.dsokOrders.external.deliveryTypePickup",
};

export const getDeliveryTypeLabelKey = (deliveryType) =>
  DELIVERY_TYPE_LABEL_KEYS[String(deliveryType || "").toLowerCase()] || null;

const PAYMENT_PLACE_LABEL_KEYS = {
  onpickup: "pages.dsokOrders.external.paymentOnPickup",
  instore: "pages.dsokOrders.external.paymentInStore",
};

/** onPickup/inStore → localized "payment on site" label key (unpaid orders). */
export const getPaymentPlaceLabelKey = (paymentMethod) =>
  PAYMENT_PLACE_LABEL_KEYS[String(paymentMethod || "").toLowerCase()] || null;

/** Human channel label; falls back to the raw channel value. */
export const getSourceChannelLabelKey = (channel) => {
  const c = String(channel || "").toLowerCase();
  if (c === "phone") return "pages.dsokOrders.external.channelPhone";
  return null;
};

/** Zobrazovaný názov kanála (zdroja) externej objednávky. */
export const getSourceChannelDisplay = (channel, t) => {
  const c = String(channel || "").toLowerCase();
  if (c === "phone")
    return t("pages.dsokOrders.external.channelPhone", {
      defaultValue: "Telefón",
    });
  if (c === "web")
    return t("pages.dsokOrders.external.channelWeb", { defaultValue: "Web" });
  if (c === "audioecho") return "AudioEcho";
  if (c === "woocommerce") return "WooCommerce";
  return (
    channel ||
    t("pages.dsokOrders.external.badge", { defaultValue: "Externá" })
  );
};

/** Názov ikony podľa kanála/zdroja: "phone" | "store" | "external". */
export const getSourceChannelIconName = (channel) => {
  const c = String(channel || "").toLowerCase();
  if (c === "audioecho" || c === "phone") return "phone";
  if (c === "woocommerce" || c === "web") return "store";
  return "external";
};

/** Compose a one-line address string from orderCustomer.address. */
export const formatOrderCustomerAddress = (address) => {
  if (!address || typeof address !== "object") return "";
  const line1 = [address.street, address.houseNumber].filter(Boolean).join(" ");
  const line2 = [address.zip, address.city].filter(Boolean).join(" ");
  return [line1, line2, address.country].filter(Boolean).join(", ");
};
